import React, { useEffect, useState } from "react";
import useLogin from "../hooks/useLogin";
import { checkIfAuthenticated } from "../utils/utils";
import { GoAlertFill } from "react-icons/go";
import { AiOutlineLoading } from "react-icons/ai";

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const { loading, error, login } = useLogin();

  // `?expirada=1` é posto pelo interceptor do axios quando a sessão morre.
  const sessionExpired = new URLSearchParams(window.location.search).has(
    "expirada"
  );

  /*
    Quem já está logado não vê a tela de login.

    O redirecionamento ficava no CORPO do componente, o que é efeito colateral
    durante a renderização — em modo estrito do React a função roda duas vezes
    e a navegação disparava no meio do render.
  */
  useEffect(() => {
    if (checkIfAuthenticated()) window.location.replace("/");
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      /*
        O redirecionamento usa o token DEVOLVIDO pelo login, não o estado
        `isLoggedIn`. O código antigo lia o estado logo depois do await — e
        estado do React só vale no render seguinte, então a condição era
        sempre falsa e nada acontecia no primeiro clique: a pessoa digitava
        certo, nada aparecia, e tentava de novo.
      */
      const token = await login(email, password);
      if (token) window.location.replace("/");
    } catch (err) {
      // A mensagem para a pessoa sai do `error` do hook, no formulário.
    }
  };

  return (
    <>
      <style>{`
        .lp-root {
          display: flex;
          height: 100vh;
          overflow: hidden;
        }
        .lp-brand {
          flex: 0 0 45%;
          background: var(--gray);
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          padding: 48px 56px;
          position: relative;
          overflow: hidden;
        }
        .lp-form-panel {
          flex: 1;
          background: #f8f8f9;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 48px 40px;
          overflow-y: auto;
        }
        .lp-mobile-logo {
          display: none;
          margin-bottom: 32px;
          text-align: center;
        }

        /* Tablet */
        @media (max-width: 1024px) {
          .lp-brand {
            flex: 0 0 38%;
            padding: 40px 36px;
          }
          .lp-brand h2 {
            font-size: 26px !important;
          }
        }

        /* Mobile */
        @media (max-width: 767px) {
          .lp-root {
            flex-direction: column;
            height: auto;
            min-height: 100vh;
            overflow: auto;
          }
          .lp-brand {
            display: none;
          }
          .lp-form-panel {
            flex: 1;
            padding: 48px 24px 40px;
            align-items: flex-start;
            padding-top: 56px;
          }
          .lp-mobile-logo {
            display: block;
          }
        }
      `}</style>

      <div className="lp-root">
        {/* Left panel — brand */}
        <div className="lp-brand">
          {/* Decorative circles */}
          <div style={{ position: "absolute", top: -120, right: -120, width: 400, height: 400, borderRadius: "50%", border: "1px solid rgba(255,255,255,0.06)", pointerEvents: "none" }} />
          <div style={{ position: "absolute", top: -60, right: -60, width: 240, height: 240, borderRadius: "50%", border: "1px solid rgba(255,255,255,0.08)", pointerEvents: "none" }} />
          <div style={{ position: "absolute", bottom: -140, left: -100, width: 360, height: 360, borderRadius: "50%", border: "1px solid rgba(187,38,73,0.18)", pointerEvents: "none" }} />
          <div style={{ position: "absolute", bottom: -80, left: -40, width: 200, height: 200, borderRadius: "50%", background: "rgba(187,38,73,0.07)", pointerEvents: "none" }} />

          {/* Logo */}
          <div>
            <img src="/assets/logo-branca.png" alt="Logo" style={{ height: 44, objectFit: "contain" }} />
          </div>

          {/* Center content */}
          <div style={{ zIndex: 1 }}>
            <div style={{ display: "inline-block", background: "rgba(187,38,73,0.18)", border: "1px solid rgba(187,38,73,0.35)", borderRadius: 6, padding: "4px 12px", marginBottom: 20 }}>
              <span style={{ color: "var(--secondary)", fontSize: 11, fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase" }}>
                Painel Administrativo
              </span>
            </div>

            <h2 style={{ color: "#ffffff", fontSize: 32, fontWeight: 700, lineHeight: 1.25, marginBottom: 16 }}>
              Gestão centralizada
              <br />
              <span style={{ color: "var(--secondary)" }}>para o seu negócio</span>
            </h2>

            <p style={{ color: "rgba(255,255,255,0.45)", fontSize: 14, lineHeight: 1.7, maxWidth: 340 }}>
              Acesse métricas em tempo real, gerencie colaboradores e acompanhe o
              crescimento da sua operação em um só lugar.
            </p>
          </div>

          {/* Footer */}
          <div>
            <p style={{ color: "rgba(255,255,255,0.2)", fontSize: 12 }}>
              © {new Date().getFullYear()} Relinq · Todos os direitos reservados
            </p>
          </div>
        </div>

        {/* Right panel — form */}
        <div className="lp-form-panel">
          <div style={{ width: "100%", maxWidth: 400 }}>
            {/* Mobile-only logo */}
            <div className="lp-mobile-logo">
              <img src="/assets/logo-preta.png" alt="Logo" style={{ height: 36, objectFit: "contain" }} />
            </div>

            {/* Header */}
            <div style={{ marginBottom: 36 }}>
              <h1 style={{ color: "#1a1a2e", fontSize: 24, fontWeight: 700, marginBottom: 6, letterSpacing: "-0.02em" }}>
                Bem-vindo de volta
              </h1>
              <p style={{ color: "#8a8f9e", fontSize: 14 }}>
                Insira suas credenciais para acessar o painel.
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              {/* E-mail */}
              <div>
                <label
                  htmlFor="email"
                  style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#3d4152", marginBottom: 6, letterSpacing: "0.01em" }}
                >
                  E-mail
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="seu@email.com"
                  style={{ width: "100%", padding: "11px 14px", border: "1.5px solid #dde1eb", borderRadius: 8, fontSize: 14, color: "#1a1a2e", background: "#ffffff", outline: "none", transition: "border-color 0.2s", fontFamily: "inherit", boxSizing: "border-box" }}
                  onFocus={(e) => (e.target.style.borderColor = "var(--primary)")}
                  onBlur={(e) => (e.target.style.borderColor = "#dde1eb")}
                />
              </div>

              {/* Senha */}
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                  <label
                    htmlFor="password"
                    style={{ fontSize: 13, fontWeight: 600, color: "#3d4152", letterSpacing: "0.01em" }}
                  >
                    Senha
                  </label>
                  <a
                    href="https://relinq.com.br/forgot-password"
                    style={{ fontSize: 12, color: "var(--primary)", textDecoration: "none", fontWeight: 500 }}
                  >
                    Esqueceu a senha?
                  </a>
                </div>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="••••••••"
                  style={{ width: "100%", padding: "11px 14px", border: "1.5px solid #dde1eb", borderRadius: 8, fontSize: 14, color: "#1a1a2e", background: "#ffffff", outline: "none", transition: "border-color 0.2s", fontFamily: "inherit", boxSizing: "border-box" }}
                  onFocus={(e) => (e.target.style.borderColor = "var(--primary)")}
                  onBlur={(e) => (e.target.style.borderColor = "#dde1eb")}
                />
              </div>

              {sessionExpired && !error ? (
                <div style={{ display: "flex", alignItems: "center", gap: 8, background: "#fff8e6", border: "1px solid #ffe0a3", borderRadius: 7, padding: "9px 13px" }}>
                  <GoAlertFill color="#b45309" size={14} />
                  <span style={{ fontSize: 13, color: "#b45309", fontWeight: 500 }}>
                    Sua sessão expirou. Entre novamente.
                  </span>
                </div>
              ) : null}

              {/* Error */}
              {error && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, background: "#fff0f3", border: "1px solid #ffc3ce", borderRadius: 7, padding: "9px 13px" }}>
                  <GoAlertFill color="var(--primary)" size={14} />
                  <span style={{ fontSize: 13, color: "var(--primary)", fontWeight: 500 }}>
                    E-mail ou senha incorretos.
                  </span>
                </div>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                style={{ width: "100%", padding: "12px", background: loading ? "#d4869a" : "var(--primary)", color: "#ffffff", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: loading ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, transition: "background 0.2s", fontFamily: "inherit", letterSpacing: "0.01em", marginTop: 4 }}
                onMouseEnter={(e) => { if (!loading) e.currentTarget.style.background = "var(--primary-hover)"; }}
                onMouseLeave={(e) => { if (!loading) e.currentTarget.style.background = "var(--primary)"; }}
              >
                {loading && <AiOutlineLoading className="animate-spin" size={16} />}
                {loading ? "Entrando..." : "Entrar no painel"}
              </button>
            </form>

            {/* Footer info */}
            <div style={{ marginTop: 32, paddingTop: 24, borderTop: "1px solid #eaedf2", display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#34d399", flexShrink: 0 }} />
              <p style={{ fontSize: 12, color: "#a0a5b4" }}>
                Acesso restrito a usuários autorizados.
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LoginPage;
